import { useState } from "react"
import {
  Dialog,
  DialogTrigger,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from "@/components/ui/Select"
import { UserPlus, Mail, Shield, Briefcase } from "lucide-react"
import { cn } from "src/utils/Functions"
import type { InviteParticipantFormData, ParticipantRole, PermissionLevel } from "@/types/deal.types"
import { PARTICIPANT_ROLES, PERMISSION_LEVELS } from "@/constants/deal.constants"

interface InviteParticipantDialogProps {
  dealName?: string
  onInvite?: (data: InviteParticipantFormData) => void
  trigger?: React.ReactNode
}

export function InviteParticipantDialog({
  dealName = "this deal",
  onInvite,
  trigger,
}: InviteParticipantDialogProps) {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<InviteParticipantFormData>({
    email: "",
    name: "",
    role: "collaborator",
    permission: "review",
    message: "",
  })
  const [errors, setErrors] = useState<Partial<Record<keyof InviteParticipantFormData, string>>>({})

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InviteParticipantFormData, string>> = {}

    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email"
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    onInvite?.(formData)

    setFormData({
      email: "",
      name: "",
      role: "collaborator",
      permission: "review",
      message: "",
    })
    setErrors({})
    setOpen(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setFormData({
        email: "",
        name: "",
        role: "collaborator",
        permission: "review",
        message: "",
      })
      setErrors({})
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger render={(
        <Button variant="ghost" size="sm" className="text-xs h-7">
          <UserPlus className="size-3.5 mr-1" />
          Invite
        </Button>
      )} />

      <DialogPopup className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="size-5 text-muted-foreground" />
            Invite Participant
          </DialogTitle>
          <DialogDescription>
            Add a team member or external party to collaborate on {dealName}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email" className="text-sm font-medium">
              <Mail className="size-3.5 text-muted-foreground" aria-hidden="true" />
              Email Address
            </Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="colleague@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              spellCheck={false}
              autoComplete="email"
              className={cn(errors.email && "border-destructive")}
            />
            {errors.email && (
              <p className="text-destructive text-xs">{errors.email}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-name" className="text-sm font-medium">
              Full Name
            </Label>
            <Input
              id="invite-name"
              type="text"
              placeholder="John Smith"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              autoComplete="name"
              className={cn(errors.name && "border-destructive")}
            />
            {errors.name && (
              <p className="text-destructive text-xs">{errors.name}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                <Briefcase className="size-3.5 text-muted-foreground" aria-hidden="true" />
                Role
              </Label>
              <Select
                items={PARTICIPANT_ROLES}
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value as ParticipantRole })}
              >
                <SelectTrigger className="">
                  <SelectValue />
                </SelectTrigger>
                <SelectPopup>
                  {PARTICIPANT_ROLES.map((role) => (
                    <SelectItem key={role.label} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
              <p className="text-muted-foreground text-xs">
                {PARTICIPANT_ROLES.find(r => r.value === formData.role)?.description}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                <Shield className="size-3.5 text-muted-foreground" aria-hidden="true" />
                Permission Level
              </Label>
              <Select
                items={PERMISSION_LEVELS}
                value={formData.permission}
                onValueChange={(value) => setFormData({ ...formData, permission: value as PermissionLevel })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectPopup>
                  {PERMISSION_LEVELS.map((level) => (
                    <SelectItem key={level.label} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
              <p className="text-muted-foreground text-xs">
                {PERMISSION_LEVELS.find(p => p.value === formData.permission)?.description}
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-message" className="text-sm font-medium">
              Personal Message <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <textarea
              id="invite-message"
              name="message"
              placeholder="Add a note to include with the invitation…"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              autoComplete="off"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-xs ring-ring/24 placeholder:text-muted-foreground/64 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:outline-none resize-none"
            />
          </div>

          <DialogFooter>
            <DialogClose
              render={
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              }
            />
            <Button type="submit">
              <UserPlus className="size-3 mr-0.5" />
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  )
}

export default InviteParticipantDialog
